import axios from '@/plugins/axios';
import utils from '@/stores/utils';

const state = {
    products: [],
    providers: [],
    prices: {},
    loading: false,
    loading_errors: null,
    count: 0,
    filters: {
        product_search: "",
        provider_search: "",
        providers: [],
        producttypes: [],
        low_stock: false,
        limit: 20,
        offset: 0,
    },
    selectedDeliverySite: null,
    // Store quantities to order: { productId: { providerId: quantity } }
    orderQuantities: {},
};

const mutations = {
    updateProducts(state, products) {
        // Create a new array to ensure reactivity
        state.products = Array.isArray(products) ? [...products] : [];
    },
    updateProviders(state, providers) {
        // Create a new array to ensure reactivity
        state.providers = Array.isArray(providers) ? [...providers] : [];
    },
    updatePrices(state, prices) {
        // Create a new object to ensure reactivity
        state.prices = prices ? { ...prices } : {};
    },
    updateCount(state, count) {
        state.count = count || 0;
    },
    updateLoading(state, loading) {
        state.loading = loading;
    },
    updateLoadingErrors(state, errors) {
        state.loading_errors = errors;
        state.loading = false;
    },
    updateFilters(state, filters) {
        state.filters = { ...filters};
    },
    updateOrderQuantity(state, { productId, providerId, quantity }) {
        // Use Vue.set to ensure reactivity for nested objects
        if (!state.orderQuantities[productId]) {
            state.orderQuantities = {
                ...state.orderQuantities,
                [productId]: {}
            };
        }
        if (quantity === 0) {
            const newProductQuantities = { ...state.orderQuantities[productId] };
            delete newProductQuantities[providerId];
            if (Object.keys(newProductQuantities).length === 0) {
                const newOrderQuantities = { ...state.orderQuantities };
                delete newOrderQuantities[productId];
                state.orderQuantities = newOrderQuantities;
            } else {
                state.orderQuantities = {
                    ...state.orderQuantities,
                    [productId]: newProductQuantities
                };
            }
        } else {
            state.orderQuantities = {
                ...state.orderQuantities,
                [productId]: {
                    ...state.orderQuantities[productId],
                    [providerId]: quantity
                }
            };
        }
    },
    clearOrderQuantities(state) {
        state.orderQuantities = {};
    },
    clearProviderFromCart(state, providerId) {
        // Remove all quantities for a specific provider
        const newOrderQuantities = {};
        for (const productId in state.orderQuantities) {
            const productQuantities = { ...state.orderQuantities[productId] };
            delete productQuantities[providerId];
            // Only keep the product if it has other providers
            if (Object.keys(productQuantities).length > 0) {
                newOrderQuantities[productId] = productQuantities;
            }
        }
        state.orderQuantities = newOrderQuantities;
    },
    updateSelectedDeliverySite(state, deliverysite) {
        state.selectedDeliverySite = deliverysite;
    },
};

const actions = {
    async fetchShopData({ commit, state }) {
        await commit('updateLoadingErrors', null);
        await commit('updateLoading', true);

        try {
            // Build params object, handling arrays properly
            const params = { ...state.filters };
            
            // Handle providers array
            if (params.providers && Array.isArray(params.providers) && params.providers.length > 0) {
                params.providers = params.providers;
            } else {
                delete params.providers;
            }
            
            // Handle producttypes array
            if (params.producttypes && Array.isArray(params.producttypes) && params.producttypes.length > 0) {
                params.producttypes = params.producttypes;
            } else {
                delete params.producttypes;
            }
            
            // Add stock parameter if selected
            if (state.selectedStock && state.selectedStock.id) {
                params.stock = state.selectedStock.id;
            }
            
            // Add deliverysite parameter if selected
            // The backend will use the deliverysite's stock if it has one
            if (state.selectedDeliverySite && state.selectedDeliverySite.id) {
                params.deliverysite = state.selectedDeliverySite.id;
            }
            
            const response = await axios.get('/api/shop/', { params });
            // Ensure we create new arrays/objects for reactivity
            commit('updateProducts', response.data.products || []);
            commit('updateProviders', response.data.providers || []);
            commit('updatePrices', response.data.prices || {});
            commit('updateCount', response.data.count || 0);
            commit('updateLoading', false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateLoadingErrors', error.details);
            throw error;
        }
    },
    async changeFilters({ commit, dispatch }, filters) {
        await commit('updateFilters', filters);
        await dispatch('fetchShopData');
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};

